import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  cruiseCountdownSnapshot,
  parseTripStartLocal,
  type CruiseCountdownSnapshot,
} from '../../utils/cruiseCountdownCalculator';
import { tripsEn } from '../../content/trips.en';
import type { TripType } from '../../types/client';
import { FerryIcon, PinIcon } from '../icons/TripIcons';
import styles from './CruiseCountdownCard.module.css';

interface CruiseCountdownCardProps {
  tripId?: string;
  tripName: string;
  tripType: TripType | null;
  startDate: string;
  /** When false, hide trip name header (TripDetail compact style). Default true. */
  showsHeader?: boolean;
}

const CruiseCountdownCard: React.FC<CruiseCountdownCardProps> = ({
  tripId,
  tripName,
  tripType,
  startDate,
  showsHeader = true,
}) => {
  const [snapshot, setSnapshot] = useState<CruiseCountdownSnapshot | null>(null);

  useEffect(() => {
    const targetDate = parseTripStartLocal(startDate);
    if (!targetDate) {
      setSnapshot(null);
      return;
    }
    const tick = () => setSnapshot(cruiseCountdownSnapshot(new Date(), targetDate));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [startDate]);

  const target = parseTripStartLocal(startDate);
  if (!target || !snapshot || snapshot.hasDeparted) return null;

  const units = (
    <div className={styles.units}>
      {snapshot.units.map((unit, index) => (
        <React.Fragment key={unit.labelKey}>
          {index > 0 ? <span className={styles.colon}>:</span> : null}
          <div className={styles.unit}>
            <div className={styles.unitValue}>{unit.value}</div>
            <div className={styles.unitLabel}>{tripsEn.countdown[unit.labelKey]}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  const className = showsHeader ? styles.card : `${styles.card} ${styles.cardCompact}`;

  if (!showsHeader || !tripId) {
    return <div className={className}>{units}</div>;
  }

  return (
    <Link to={`/trips/${tripId}`} className={className}>
      <div className={styles.header}>
        <div className={styles.typeIcon} aria-hidden>
          {tripType === 'cruise' ? <FerryIcon size={20} /> : <PinIcon size={20} />}
        </div>
        <div className={styles.headerText}>
          <div className={styles.eyebrow}>{tripsEn.countdown.title}</div>
          <div className={styles.tripName}>{tripName}</div>
        </div>
        <span className={styles.chevron} aria-hidden>
          ›
        </span>
      </div>
      {units}
    </Link>
  );
};

export default CruiseCountdownCard;
